import { apiCall, ApiError } from '@/lib/api-client';
import type {
  ChangeComment, CollabDraft, CollabDraftContent, CollabDiffEntry, CollabVersion,
  CollabVersionsResponse, DraftLock, CommentTarget, ReviewDecisionRequest, ReviewDecisionsPage, ReviewChangeList } from './collabTypes';

const base = (ref: string) => `/api/vault/documents/${encodeURIComponent(ref)}`;

export const getVersions = (ref: string, includeClosed = false) =>
  apiCall<CollabVersionsResponse>(
    `${base(ref)}/versions${includeClosed ? '?include_closed=true' : ''}`);

export const getDraft = (ref: string) =>
  apiCall<CollabDraftContent>(`${base(ref)}/draft`);

/** 404 → null: no working copy is open (force-unlocked / discarded). */
export const getDraftLock = async (ref: string): Promise<DraftLock | null> => {
  try {
    return await apiCall<DraftLock>(`${base(ref)}/draft/lock`);
  } catch (e) {
    if (e instanceof ApiError && e.status === 404) return null;
    throw e;
  }
};

export const getDraftDiff = (ref: string) =>
  apiCall<CollabDiffEntry[]>(`${base(ref)}/draft/diff`);

export const promoteRun = (ref: string, run_id: number, reason: string) =>
  apiCall<CollabVersion>(`${base(ref)}/promote`, { method: 'POST', json: { run_id, reason } });

export const checkoutDraft = (ref: string) =>
  apiCall<CollabDraft>(`${base(ref)}/checkout`, { method: 'POST' });

export const takeOverDraft = (ref: string) =>
  apiCall<CollabDraft>(`${base(ref)}/take-over`, { method: 'POST' });

/** Replace the file on a file-backed (upload) working copy — multipart `file=`. */
/**
 * Save the working copy's file. `baseUpdatedAt` is the `updated_at` this window loaded the copy
 * with; the server refuses (409) a save based on a copy that has since changed in another window
 * instead of silently overwriting it. Always send it — the lock is per account, so two windows
 * of the same person are the exact case.
 */
export const saveDraftFile = (ref: string, file: File, baseUpdatedAt: string | null) => {
  const form = new FormData();
  form.append('file', file);
  if (baseUpdatedAt) form.append('base_updated_at', baseUpdatedAt);
  return apiCall<CollabDraft>(`${base(ref)}/draft/file`, { method: 'POST', bodyRaw: form });
};

export const submitDraft = (ref: string, reason: string) =>
  apiCall<CollabDraft>(`${base(ref)}/draft/submit`, { method: 'POST', json: { reason } });

export const discardDraft = (ref: string) =>
  apiCall<null>(`${base(ref)}/draft`, { method: 'DELETE', allow204: true });

export const approveDraft = (ref: string, password: string, reason = '') =>
  apiCall<CollabVersion>(`${base(ref)}/draft/approve`, {
    method: 'POST',
    json: { password, reason },
  });

export const rejectDraft = (ref: string, reason: string, comments: ChangeComment[] = []) =>
  apiCall<CollabDraft>(`${base(ref)}/draft/reject`, {
    method: 'POST',
    json: { reason, comments },
  });

/** A reviewer annotates the in-review working copy WITHOUT sending it back — the doc
 *  stays in review and the comment lands in the persistent review thread. */
export const commentDraft = (ref: string, body: string, target: CommentTarget | null = null) =>
  apiCall<CollabDraft>(`${base(ref)}/draft/comments`, {
    method: 'POST',
    json: target ? { body, revision_id: target.revisionId, change_text: target.changeText } : { body },
  });

/** The editor replies to a reviewer comment in the same thread — no send-back, the
 *  doc stays in review. */
export const replyDraft = (ref: string, body: string, target: CommentTarget | null = null) =>
  apiCall<CollabDraft>(`${base(ref)}/draft/replies`, {
    method: 'POST',
    json: target ? { body, revision_id: target.revisionId, change_text: target.changeText } : { body },
  });

/** The review round's per-change decisions (reviewer-only, in-review only). */
export const getReviewDecisions = (ref: string) =>
  apiCall<ReviewDecisionsPage>(`${base(ref)}/draft/decisions`);

/** The tracked changes in the open working copy, with no decisions — what the EDITOR's rail
 *  reads, so both rails describe the file the same way. */
export const getDraftChanges = (ref: string) =>
  apiCall<ReviewChangeList>(`${base(ref)}/draft/changes`);

/** Record, replace or withdraw (decision null) the reviewer's decision on one tracked change. */
export const putReviewDecision = (ref: string, revisionId: number, body: ReviewDecisionRequest) =>
  apiCall<ReviewDecisionsPage>(`${base(ref)}/draft/decisions/${revisionId}`, { method: 'PUT', json: body });

/** Same-origin download/preview URL for a controlled VERSION (not a run). ``cert``
 *  returns the standalone Part-11 signature certificate (file-backed signed versions). */
export const versionDownloadUrl = (ref: string, versionId: number, inline = false, cert = false) =>
  `${base(ref)}/download?version_kind=collab&version=${versionId}` +
  `${inline ? '&inline=1' : ''}${cert ? '&cert=1' : ''}`;

export const auditExportUrl = (ref: string) => `${base(ref)}/audit/export`;
